import {type QueryClient} from '@tanstack/react-query'

import {createQueryKey} from '#/state/queries/util'

export const classroomKeys = {
  room: (id: string) => createQueryKey('classroom', {id}),
  byRoute: (owner: string, handle: string) =>
    createQueryKey('classroomByRoute', {owner, handle}),
  members: (id: string, role?: string) =>
    createQueryKey('classroomMembers', {id, role}),
  requests: (id: string) => createQueryKey('classroomRequests', {id}),
  content: (id: string, kind?: string) =>
    createQueryKey('classroomContent', {id, kind}),
  forDid: (did: string, relation: string) =>
    createQueryKey('classroomsForDid', {did, relation}),
  roles: (did: string) => createQueryKey('classroomViewerRoles', {did}),
  search: (q: string) => createQueryKey('classroomSearch', {q}),
  suggested: () => createQueryKey('classroomSuggested', {}),
}

/*
 * Root-only prefixes for invalidation. A keyed `classroomKeys.members(id)` carries
 * `role: undefined`, which does not partially match a key that has a role, so
 * invalidation goes through these.
 */
export const classroomKeyPrefixes = {
  room: (id?: string) =>
    (id ? ['classroom', {id}] : ['classroom']) as readonly unknown[],
  byRoute: () => ['classroomByRoute'] as const,
  members: (id: string) => ['classroomMembers', {id}] as const,
  requests: (id: string) => ['classroomRequests', {id}] as const,
  content: (id: string) => ['classroomContent', {id}] as const,
  forDid: () => ['classroomsForDid'] as const,
  roles: () => ['classroomViewerRoles'] as const,
}

/**
 * One classroom changed on the server. Every cache that can show it is
 * refreshed, since a role, a count or a roster change ripples into lists, the
 * route lookup and the viewer's roles. Correctness over precision.
 */
export function invalidateClassroom(queryClient: QueryClient, id: string) {
  const prefixes = [
    classroomKeyPrefixes.room(id),
    classroomKeyPrefixes.byRoute(),
    classroomKeyPrefixes.members(id),
    classroomKeyPrefixes.requests(id),
    classroomKeyPrefixes.content(id),
    classroomKeyPrefixes.forDid(),
    classroomKeyPrefixes.roles(),
  ]
  return Promise.all(
    prefixes.map(queryKey => queryClient.invalidateQueries({queryKey})),
  )
}
